// FILE: src/components/admin/SeoHealthScreen.js
//
// Admin › SEO Health — the Content Factory signal ladder on one screen:
// indexing coverage, impressions/clicks/position from Search Console,
// emerging queries, and content-attributed arrivals/conversions from
// PM Pixel telemetry. GSC and telemetry sections load and fail
// independently: a Google outage never blanks the telemetry side.
//
// SCOPE IS THE THING TO GET RIGHT HERE. Search Console gives us two different
// populations and they look identical on a tile:
//
//   • site-wide — every page on the property (homepage, app, landing pages)
//   • article   — the Content Factory articles from the sitemap
//
// The tiles used to show the SITE-WIDE totals unlabelled on a screen titled
// "Content Factory signal ladder", which read as the content working when the
// articles had zero clicks in 28 days and every click belonged to the homepage.
// Every tile now names its scope, and "first article click" is keyed on article
// clicks. If you add a tile here, label its scope or don't add it.

import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchGscMetrics,
  fetchContentSeoHealth,
  deriveSignalLadder,
} from '../../services/seoHealthService';

const S = {
  wrap: { padding: '18px 22px 40px', maxWidth: 1100, margin: '0 auto', font: '14px/1.5 "Segoe UI", system-ui, sans-serif', color: '#1a2138' },
  headerRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 },
  back: { border: '1px solid #cfd5e6', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' },
  title: { fontSize: 20, fontWeight: 700, margin: 0, flex: 1 },
  refresh: { border: '1px solid #2c63ff', color: '#2c63ff', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' },
  meta: { color: '#5a6582', fontSize: 12, margin: '0 0 16px' },
  scopeNote: { color: '#5a6582', fontSize: 12, lineHeight: 1.6, margin: '-10px 0 20px', maxWidth: 820 },
  tableNote: { color: '#5a6582', fontSize: 11, lineHeight: 1.6, margin: '6px 0 0' },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 22 },
  card: { background: '#fff', border: '1px solid #e5e8f1', borderRadius: 10, padding: '12px 14px' },
  cardLabel: { fontSize: 12, color: '#5a6582', marginBottom: 4 },
  cardValue: { fontSize: 22, fontWeight: 700 },
  cardHint: { fontSize: 11, color: '#5a6582' },
  section: { fontSize: 15, fontWeight: 700, margin: '20px 0 8px' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: 13 },
  th: { textAlign: 'left', borderBottom: '2px solid #cfd5e6', padding: '7px 9px', color: '#5a6582', whiteSpace: 'nowrap' },
  td: { borderBottom: '1px solid #e5e8f1', padding: '7px 9px', verticalAlign: 'top' },
  err: { background: '#fde9ea', border: '1px solid #d64545', borderRadius: 8, padding: '10px 14px', margin: '8px 0', fontSize: 13 },
  pill: (ok) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12,
    background: ok ? '#e6f7ef' : '#fff4dc', color: ok ? '#16a571' : '#7a5a16',
  }),
};

function shortPage(url) {
  try { return new URL(url).pathname.replace('/articles/', '').replace(/\/$/, '') || '/'; }
  catch (_) { return url; }
}

export default function SeoHealthScreen({ onBack }) {
  const [gsc, setGsc] = useState(null);
  const [gscError, setGscError] = useState(null);
  const [content, setContent] = useState(null);
  const [contentError, setContentError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (refresh) => {
    setLoading(true);
    const [gscRes, contentRes] = await Promise.allSettled([
      fetchGscMetrics({ refresh }),
      fetchContentSeoHealth(7),
    ]);
    if (gscRes.status === 'fulfilled') { setGsc(gscRes.value); setGscError(null); }
    else setGscError(gscRes.reason?.message || 'Search Console fetch failed');
    if (contentRes.status === 'fulfilled') { setContent(contentRes.value); setContentError(null); }
    else setContentError(contentRes.reason?.message || 'Telemetry fetch failed');
    setLoading(false);
  }, []);

  useEffect(() => { load(false); }, [load]);

  const ladder = deriveSignalLadder(gsc, content);
  const items = content?.content || [];

  return (
    <div style={S.wrap} data-testid="seo-health-screen">
      <div style={S.headerRow}>
        <button style={S.back} onClick={onBack} data-testid="seo-health-back">← Back</button>
        <h1 style={S.title}>SEO Health</h1>
        <button style={S.refresh} onClick={() => load(true)} disabled={loading} data-testid="seo-health-refresh">
          {loading ? 'Loading…' : '⟳ Refresh live'}
        </button>
      </div>
      <p style={S.meta}>
        Signal ladder for the Content Factory: indexed → impressions → target queries → position → clicks → attributed arrivals.
        {gsc?.fetchedAt ? ` GSC data fetched ${new Date(gsc.fetchedAt).toLocaleString()}${gsc.cached ? ' (cached)' : ''}. ` : ' '}
        {gsc?.dataLagNote || ''}
      </p>

      <div style={S.cards}>
        <div style={S.card} data-testid="tile-indexed">
          <div style={S.cardLabel}>Indexed articles</div>
          <div style={S.cardValue}>{ladder.indexed}</div>
          <div style={S.cardHint}>via URL Inspection</div>
        </div>
        <div style={S.card} data-testid="tile-article-impressions">
          <div style={S.cardLabel}>Article impressions (7d)</div>
          <div style={S.cardValue}>{ladder.articleImpressions7 ?? '—'}</div>
          <div style={S.cardHint}>
            {gsc ? `28d: ${ladder.articleImpressions28} · site-wide 7d: ${ladder.siteImpressions7}` : ''}
          </div>
        </div>
        <div style={S.card} data-testid="tile-article-clicks">
          <div style={S.cardLabel}>Article clicks (7d)</div>
          <div style={S.cardValue}>{ladder.articleClicks7 ?? '—'}</div>
          <div style={S.cardHint}>
            {gsc
              ? `${ladder.firstOrganicClick ? 'first article click ✓' : 'no article clicks yet'} · site-wide 7d: ${ladder.siteClicks7}`
              : ''}
          </div>
        </div>
        <div style={S.card} data-testid="tile-organic-arrivals">
          <div style={S.cardLabel}>Organic arrivals</div>
          <div style={S.cardValue}>{ladder.organicArrivals}</div>
          <div style={S.cardHint}>search → article → app</div>
        </div>
        <div style={S.card} data-testid="tile-attributed-regs">
          <div style={S.cardLabel}>Attributed registrations</div>
          <div style={S.cardValue}>{ladder.attributedRegistrations}</div>
          <div style={S.cardHint}>content visitors, any source</div>
        </div>
      </div>

      <p style={S.scopeNote} data-testid="seo-health-scope-note">
        <strong>Article</strong> figures cover the {(gsc?.pages || []).length || 'six'} Content Factory
        articles only. <strong>Site-wide</strong> covers every page on the property — homepage, app and
        landing pages included — so site-wide clicks can be healthy while the articles have earned none.
        Registrations are attributed to content visitors from any source, not only search; the organic
        arrivals tile is the search-only count.
      </p>

      {gscError && <div style={S.err} data-testid="seo-health-gsc-error">Search Console: {gscError}</div>}
      {contentError && <div style={S.err} data-testid="seo-health-telemetry-error">Telemetry: {contentError}</div>}

      <div style={S.section}>Per-article health</div>
      <table style={S.table} data-testid="seo-health-pages">
        <thead><tr>
          <th style={S.th}>Article</th><th style={S.th}>Index state</th>
          <th style={S.th}>Impr. 7d</th><th style={S.th}>Impr. 28d</th><th style={S.th}>Clicks 28d</th><th style={S.th}>Avg pos.</th>
        </tr></thead>
        <tbody>
          {(!gsc || (gsc.pages || []).length === 0) && (
            <tr><td style={S.td} colSpan={6}>{gscError ? 'Search Console unavailable.' : 'Loading Search Console data…'}</td></tr>
          )}
          {(gsc?.pages || []).map((p) => {
            const indexed = /indexed/i.test(p.inspection?.coverageState || '') && !/not indexed/i.test(p.inspection?.coverageState || '');
            return (
              <tr key={p.url}>
                <td style={S.td}>{shortPage(p.url)}</td>
                <td style={S.td}><span style={S.pill(indexed)}>{p.inspection?.coverageState || 'unknown'}</span></td>
                <td style={S.td}>{p.impressions7}</td>
                <td style={S.td}>{p.impressions28}</td>
                <td style={S.td}>{p.clicks28}</td>
                <td style={S.td}>{p.position28 ? p.position28.toFixed(1) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p style={S.tableNote}>
        Impressions here are page-attributed by Search Console and deliberately will not reconcile with
        the site-wide totals above. Average position from a handful of impressions is noise, not a ranking.
      </p>

      <div style={S.section}>Content attribution (telemetry)</div>
      <table style={S.table} data-testid="seo-health-content">
        <thead><tr>
          <th style={S.th}>Content ID</th><th style={S.th}>Arrivals 7d</th><th style={S.th}>Total</th>
          <th style={S.th}>Visitors</th><th style={S.th}>Organic</th><th style={S.th}>Campaign</th>
          <th style={S.th}>Regs</th><th style={S.th}>First practice</th><th style={S.th}>Top CTAs</th>
        </tr></thead>
        <tbody>
          {items.length === 0 && (
            <tr><td style={S.td} colSpan={9}>{content ? 'No content-attributed arrivals yet.' : 'Loading…'}</td></tr>
          )}
          {items.map((c) => (
            <tr key={c.content_id}>
              <td style={S.td}>{c.content_id}</td>
              <td style={S.td}>{c.arrivals_window}</td>
              <td style={S.td}>{c.arrivals_total}</td>
              <td style={S.td}>{c.visitors_total}</td>
              <td style={S.td}>{c.organic_total}</td>
              <td style={S.td}>{c.campaign_total}</td>
              <td style={S.td}>{c.registrations}</td>
              <td style={S.td}>{c.first_practices}</td>
              <td style={S.td}>{Object.entries(c.ctas || {}).sort((a, b) => b[1] - a[1]).slice(0, 3)
                .map(([k, v]) => `${k}:${v}`).join(' · ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={S.section}>Top queries (28d)</div>
      <table style={S.table} data-testid="seo-health-queries">
        <thead><tr>
          <th style={S.th}>Query</th><th style={S.th}>Impressions</th><th style={S.th}>Clicks</th><th style={S.th}>Avg position</th>
        </tr></thead>
        <tbody>
          {(!gsc || (gsc.queries || []).length === 0) && (
            <tr><td style={S.td} colSpan={4}>No query impressions yet — normal for a young property. Watch for target-intent queries appearing here first.</td></tr>
          )}
          {(gsc?.queries || []).map((q) => (
            <tr key={q.query}>
              <td style={S.td}>{q.query}</td>
              <td style={S.td}>{q.impressions}</td>
              <td style={S.td}>{q.clicks}</td>
              <td style={S.td}>{q.position ? q.position.toFixed(1) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
